<template>
  <ProductDetailAddToCart
    v-if="!productStore.decoratedProduct?.isDiscontinued"
    :class="showTariffInformation ? 'show-tariff-information' : ''"
    :v-model="1"
    :min-amount="1"
    :max-amount="10"
    :amount-label="$textDictionary['pdp.amount.label'] ?? 'Amount'"
    :add-to-cart-button-label="
      $textDictionary['pdp.addToCart.label'] ?? 'Add to Cart'
    "
    :request-button-label="
      $textDictionary['pdp.requestButton.label'] ?? 'Request individual quote'
    "
    :header-link-text="headerLinkText"
    :header-link="headerLink"
    :show-configurator-link="productStore.decoratedProduct?.showCSV"
    :configurator-link-text="csvLinkText"
    :configurator-link="csvLink"
    :show-add-to-cart-button="displayAddToCartButton"
    :is-add-to-cart-button-disabled="
      productPriceStore.isAddToCartButtonDisabled || disableAddToCart
    "
    :show-request-button="productStore.decoratedProduct?.showRequestCartButton"
    :is-request-button-disabled="
      productPriceStore.isRequestButtonDisabled || disableRequest
    "
    :show-amount="displayAddToCartButton"
    :attributes="productDetailAttributes"
    @update:model-value="() => 0"
    @add-to-cart="(amount: number) => handleAddToCart('cart', amount)"
    @request="(amount: number) => handleAddToCart('requestCart', amount)"
  />

  <div v-if="showTariffInformation" class="rte-text mt-2 tariff-hint">
    <small> <LibRichTextRenderer :rich-text="pdpTariffText" /></small>
  </div>
</template>

<script setup lang="ts">
import { ProductDetailAddToCart } from '@hypercodestudio/basler-components';
import {
  type DeliveryOption,
  type AddToCartAttribute,
  type DeliveryOptions
} from '@hypercodestudio/basler-components/dist/components/modules/ProductDetailAddToCart.vue';
import { useCartStore } from '~/stores/cartStore/cartStore';
import type { CartType } from '~/stores/cartStore/model';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import { useProductPriceStore } from '~/stores/productDetailPageStore/productPriceStore';
import { useProductAddonsStore } from '~/stores/productDetailPageStore/productAddonsStore';
import { useHTMLDictionary } from '~/composables/useHTMLDictionary';
import { useCrossSellingProductsStore } from '~/stores/productDetailPageStore/crossSellingProductsStore';
import { useAccessoryAssistantStore } from '~/stores/productDetailPageStore/accessoryAssistantStore';

const logger = useLogger();
const pdpConfiguration = usePdpConfiguration();
const { $textDictionary, $dictionary, $shopStore } = useNuxtApp();
const productAddonStore = useProductAddonsStore();
const crossSellingProductsStore = useCrossSellingProductsStore();
const productStore = useProductDetailPageStore();
const accessoryAssistantStore = useAccessoryAssistantStore();
const cartStore = useCartStore();
const productPriceStore = useProductPriceStore();

const htmlDictionary = useHTMLDictionary([
  'pdp.shippingOption.standard.tooltip.text',
  'pdp.shippingOption.fastDelivery.tooltip.text',
  'pdp.tariff.text'
]);

const pdpTariffText = $dictionary.value['pdp.tariff.text'];

const disableAddToCart = ref(false);
const disableRequest = ref(false);
const selectedAccessories = ref(
  getVSCAccessories(crossSellingProductsStore.selectedCrossSellingSkus)
);

const displayAddToCartButton = computed(() =>
  productStore.decoratedProduct?.isBuyable()
);

const csvLinkText = computed(() =>
  productStore.decoratedProduct?.showCSV
    ? $textDictionary.value['pdp.cart.vsc.label'] ?? ''
    : ''
);

const csvLink = computed(() =>
  productStore.decoratedProduct?.getCSVLink(
    pdpConfiguration.value,
    productStore.vscPageLink,
    selectedAccessories.value
  )
);

watch(
  () => crossSellingProductsStore.selectedCrossSellingSkus,
  () => {
    if (crossSellingProductsStore.selectedCrossSellingSkus) {
      selectedAccessories.value = getVSCAccessories(
        crossSellingProductsStore.selectedCrossSellingSkus
      );
    }
  }
);

function getVSCAccessories(selectedCrossSellingSkus: readonly string[]) {
  if (selectedCrossSellingSkus.length) {
    return `;accessories=[${selectedCrossSellingSkus.toString()}]`;
  }
  return;
}

async function handleAddToCart(type: CartType, quantity: number) {
  if (!productStore.decoratedProduct) {
    logger.warn('no active product given - aborting addToCart');

    return;
  }

  disableAddToCart.value = type === 'cart';
  disableRequest.value = type === 'requestCart';

  const sku = productStore.decoratedProduct.sku;
  // workaround for proper typing
  const passedQuantity: unknown = quantity;

  if (
    !sku ||
    // empty user input
    (typeof passedQuantity === 'string' && passedQuantity.trim() === '') ||
    (typeof passedQuantity === 'number' && passedQuantity <= 0)
  ) {
    return;
  }

  const cartItems = [
    {
      decoratedProduct: productStore.decoratedProduct,
      quantity
    },
    // add additional addon-products if given
    // @see https://gcp.baslerweb.com/jira/browse/DBP-448
    ...productAddonStore.selectedOptionSkus.map((sku) => ({
      quantity: 1,
      sku
    })),
    // add additional cross-selling products if given
    // @see https://gcp.baslerweb.com/jira/browse/DBP-1176
    ...crossSellingProductsStore.selectedCrossSellingSkus.map((sku) => ({
      quantity, // same quantity as the product itself! e.g. 2 cameras with 2 cables (via cross selling, for each cam one cable)
      sku
    }))
  ];

  const accessory = accessoryAssistantStore?.selectedProduct?.sku
    ? {
        quantity: 1,
        sku: accessoryAssistantStore?.selectedProduct?.sku ?? ''
      }
    : undefined;

  if (accessory) {
    cartItems.push(accessory);
  }

  await cartStore.addProducts(cartItems, type);
  disableAddToCart.value = false;
  disableRequest.value = false;
}

const isSoftwareProduct = computed(() => {
  return (
    productStore.decoratedProduct?.product?.attribute_set_name ===
    'Pim Software (software)'
  );
});

const isVirtualService = computed(() => {
  return (
    productStore.decoratedProduct?.product?.attribute_set_name ===
    'Pim Virtual Services (virtual_services)'
  );
});

const deliveryOptions = computed((): DeliveryOption[] => {
  const options: DeliveryOption[] = [];

  if (!productStore.decoratedProduct?.showShippingTime) {
    options.push({
      iconName: isSoftwareProduct.value ? 'software' : 'available',
      title: isSoftwareProduct.value
        ? $textDictionary.value[
            'pdp.shippingOptionWithoutShippingTime.software.title'
          ] ?? 'pdp.shippingOptionWithoutShippingTime.software.title'
        : $textDictionary.value['pdp.shippingOption.standard.title'] ??
          'pdp.shippingOption.standard.title',
      titleAddition: productStore.decoratedProduct?.shippingTimeText ?? ''
    });
  } else {
    options.push({
      iconName: isSoftwareProduct.value ? 'software' : 'available',
      title: isSoftwareProduct.value
        ? $textDictionary.value['pdp.shippingOption.software.title'] ??
          'pdp.shippingOption.software.title'
        : $textDictionary.value['pdp.shippingOption.standard.title'] ?? '',
      titleAddition: productStore.decoratedProduct?.shippingTimeText ?? '',
      text: $textDictionary.value['pdp.shippingOption.standard.text'] ?? ''
    });
  }

  if (
    productStore.decoratedProduct?.showShippingTime &&
    htmlDictionary.value['pdp.shippingOption.standard.tooltip.text'] &&
    !isSoftwareProduct.value
  ) {
    options[0].tooltip = {
      tag: 'button',
      tooltipText:
        '<div class="rte-text">' +
        htmlDictionary.value['pdp.shippingOption.standard.tooltip.text'] +
        '</div>'
    };
  }

  if (productStore.decoratedProduct?.hasFastDelivery) {
    options.push({
      iconName: 'truck-fast',
      title:
        $textDictionary.value['pdp.shippingOption.fastDelivery.title'] ?? '',
      titleAddition:
        $textDictionary.value['pdp.shippingOption.fastDelivery.time'] ?? '',
      text: $textDictionary.value['pdp.shippingOption.fastDelivery.text'] ?? ''
    });
    if (htmlDictionary.value['pdp.shippingOption.fastDelivery.tooltip.text']) {
      options[1].tooltip = {
        tag: 'button',
        tooltipText:
          '<div class="rte-text">' +
          htmlDictionary.value['pdp.shippingOption.fastDelivery.tooltip.text'] +
          '</div>'
      };
    }
  }

  return options;
});

const showTariffInformation = computed(() => {
  return (
    $shopStore.value === 'amer_en' &&
    !isSoftwareProduct.value &&
    !isVirtualService.value
  );
});

const productDetailAttributes = computed(
  (): (AddToCartAttribute | DeliveryOptions)[] => {
    if (isVirtualService.value) {
      return [productPriceStore.addToCartAttribute];
    }
    return [
      productPriceStore.addToCartAttribute,
      {
        type: 'delivery-options',
        label: $textDictionary.value['pdp.shippingTime.label'] ?? '',
        options: deliveryOptions.value
      }
    ];
  }
);

const headerLinkText = computed(() => {
  if (productStore.decoratedProduct?.pdpActionBoxLinksData) {
    const headerLinkTextKey =
      ensureArray(
        productStore.decoratedProduct?.pdpActionBoxLinksData.ctf_dict_key.value
      )[0] ?? '';
    return (
      $textDictionary.value[headerLinkTextKey] ??
      ensureArray(
        productStore.decoratedProduct?.pdpActionBoxLinksData.label.value
      )[0]
    );
  }
});

const headerLink = (await productStore.enhancedPdpActionBoxLink())
  ? await productStore.enhancedPdpActionBoxLink()
  : ensureArray(
      productStore.decoratedProduct?.pdpActionBoxLinksData?.external_url?.value
    )[0]
  ? {
      uri: ensureArray(
        productStore.decoratedProduct?.pdpActionBoxLinksData?.external_url
          ?.value
      )[0],
      target: '_blank'
    }
  : '';
</script>

<style scoped>
.tariff-hint {
  padding-left: 30px;
}
</style>
<style>
.show-tariff-information
  .product-detail-add-to-cart__attribute--xlarge
  td
  div.product-detail-add-to-cart__attribute-value-box:after {
  content: '*';
  font-size: 20px;
  padding-bottom: 4px;
}
</style>
